import { ReactComponent as LogoSvg } from "@/assets/svg/intelliroom-header-logo.svg";
import { Button, Link } from "./Buttons";
import { useApp } from "@/hooks";
import { ReactComponent as HamburgerSvg } from "@/assets/svg/icon--hamburger.svg";
import { Fragment, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { DASHBOARD_SECTIONS } from "@/utils";

export const Header = () => {
  const [isOpened, setOpened] = useState(true);
  const { showContactSalesModal, setShowContactSalesModal } = useApp();
  const { screen, goToSection } = useApp();
  const { pathname } = useLocation();

  useEffect(() => {
    setOpened(false);
  }, [screen, pathname]);

  const onToggleContactSalesModal = () => {
    setShowContactSalesModal(!showContactSalesModal);
  }

  if (["", "sm", "md"].includes(screen)) {
    return (
      <div className="sticky top-0 z-50 py-4">
        <div className="stikcy-header relative z-50 header-btn-group mx-4">
          <div className=" flex items-center justify-between px-2">
            <Link className="!px-0 ml-2" to="/">
              <LogoSvg className="h-[26px]" />
            </Link>

            <div className="flex py-[4px]">
              <Button
                secondary
                className="px-5 py-[7px] md:px-[14px] md:py-[14.5px] !bg-[#303030] leading-[100%] text-[14px] !text-[#D9D9D9] font-400 flex items-center header-btn-group"
                href="https://app.intelliroom.ai"
              >
                Login
              </Button>

              <div className={`hamburger-wrapper ml-1 ${isOpened && "active"}`}>
                <button
                  className="hamburger h-full bg-secondary rounded-[8px]"
                  onClick={() => setOpened(!isOpened)}
                >
                  <HamburgerSvg />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute z-10">
          <Transition show={isOpened} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={setOpened}>
              <div className="fixed inset-0"></div>

              <div className="fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 overflow-hidden">
                  <div className="pointer-events-none fixed top-0 bottom-0 right-0 inset-y-0">
                    <TransitionChild
                      as={Fragment}
                      enter="transform transition ease-in-out duration-500"
                      enterFrom="translate-x-full" // Changed from -translate-x-full to translate-x-full
                      enterTo="translate-x-[0px]"
                      leave="transform transition ease-in-out duration-500"
                      leaveFrom="translate-x-[0px]"
                      leaveTo="translate-x-full" // Changed from -translate-x-full to translate-x-full
                    >
                      <DialogPanel className="pointer-events-auto w-screen max-w-md h-full">
                        <div className="h-full bg-white shadow-xl rounded-b-2xl">
                          <div className="h-full flex flex-col justify-between">
                            <div className="flex-1 p-5 pt-[40px] flex flex-col gap-3 items-start">
                              <div className="absolute top-5 right-5">
                                <img
                                  src="/images/home/close-circle-hover.svg"
                                  alt="Intelliroom Logo"
                                  className="h-auto w-[52px]"
                                  loading="lazy" // Added lazy loading
                                  onClick={() => setOpened(false)}
                                />
                              </div>
                              {DASHBOARD_SECTIONS.map((section, index) => (
                                <Button
                                  key={`header-section-${index}`}
                                  secondary
                                  className="px-2 py-[9.5px] !bg-transparent leading-[100%] font-calSans font-400 text-[24px] !text-black border-none"
                                  onClick={() => {
                                    setOpened(false);
                                    goToSection(section.id);
                                  }}
                                >
                                  {section.title}
                                </Button>
                              ))}
                            </div>

                            <div className="flex flex-col items-center p-5 gap-3">
                              <Button
                                className="w-full h-[48px]"
                                onClick={onToggleContactSalesModal}
                              >
                                Contact Sales
                              </Button>

                              <Button
                                className="w-full h-[48px]"
                                href="https://app.intelliroom.ai/auth/sign-up"
                              >
                                Sign up
                              </Button>
                            </div>
                          </div>
                        </div>
                      </DialogPanel>
                    </TransitionChild>
                  </div>
                </div>
              </div>
            </Dialog>
          </Transition>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full sticky top-0 z-50 py-5 lg:mt-5">
        <div className="max-w-[905px] mx-auto">
          <div className="flex items-center justify-between">
            <div className="stikcy-header flex items-center px-1.5 py-[5px] gap-x-5 header-btn-group">
              <Link className="pl-2 !pr-0 !py-0" to="/">
                <LogoSvg className="h-auto" />
              </Link>

              <div className="flex md:pl-5 border-l border-[#3E3E3E] gap-x-1.5">
                {DASHBOARD_SECTIONS.map((section, index) => (
                  <Button
                    key={`header-section-${index}`}
                    secondary
                    className="px-2 py-[9.5px] bg-transparent leading-[100%] font-400 text-[14px] !text-[#D9D9D9] border-none"
                    onClick={() => goToSection(section.id)}
                  >
                    {section.title}
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <div className="stikcy-header border rounded !border-solid hover:!border-[#262525] p-[5px] header-btn-group">
                <Button
                  secondary
                  className="!px-2 !py-[8.5px] bg-transparent !text-[#D9D9D9] border-none font-400 leading-[100%]"
                  onClick={onToggleContactSalesModal}
                >
                  Contact Sales
                </Button>
              </div>

              <div className="stikcy-header flex gap-1.5 border rounded border-btn-border p-[5px] text-[14px] header-btn-group">
                <Button
                  secondary
                  href="https://app.intelliroom.ai"
                  className="!px-2 !py-[9.5px] bg-transparent !text-[#D9D9D9] border-none font-400 leading-[100%]"
                >
                  Login
                </Button>
                <Button
                  href="https://app.intelliroom.ai/auth/sign-up"
                  className="!px-2 !py-[9.5px] !border-none text-[14px] leading-[100%]"
                >
                  Sign up
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
